import { useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { toast } from 'sonner';
import { useLmxStore } from './lmxStore';
import { useCreditsStore } from '../store/creditsStore';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Cliente del SSE de LMX. El navegador nunca habla con un modelo: sólo
 * consume /api/lmx/turn, que corre el agente en el servidor.
 */
export const useLmxTurn = () => {
  const { getToken } = useAuth();
  const s = useLmxStore;

  const handle = useCallback((name, data) => {
    const store = s.getState();
    switch (name) {
      case 'conversation':
        store.setConversation(data.id);
        break;
      case 'text_delta':
        store.appendDelta(data.text || '');
        store.setPose('talk');
        store.setStatus('Agente hablando', true);
        break;
      case 'card':
        // `live`: llegó en esta sesión (una pausa recargada con F5 no sigue sola).
        store.pushCard(data.card?.type === 'continue' ? { ...data.card, live: true } : data.card);
        break;
      case 'tool_start':
        // I-41 · `tool` es el nombre real (add_node, add_clips…): el canvas y
        // el timeline se sincronizan y animan según ESE nombre, no según el
        // texto en castellano de la fila («Agregando un nodo»).
        store.trackTool(data.id, data.label, 'go', undefined, data.tool);
        store.setPose('work');
        store.setStatus('Trabajando…', true);
        store.setBuilding(true);
        break;
      case 'tool_end':
        store.trackTool(data.id, null, data.ok ? 'ok' : 'bad', data.meta);
        break;
      case 'pose':
        store.setPose(data.value);
        break;
      case 'confirm':
        store.setPendingConfirm(data);
        store.setPose('point');
        store.setStatus('Esperando tu aprobación', true);
        break;
      case 'error':
        store.pushCard({ type: 'tag', text: data.message || 'Error', level: 'warn' });
        store.setPose('worry');
        store.setStatus('Algo falló', false, true);
        break;
      case 'done':
        store.setBuilding(false);
        break;
      // Envío LMX-C · el turno descontó créditos: el saldo de arriba al día.
      case 'billing':
        if (data?.charged) useCreditsStore.getState().fetchBalance();
        break;
      // Envío LMX-T · trabajos, cola y secciones del canvas.
      case 'jobs':
        store.setJobs(data);
        break;
      case 'queued':
        toast.success('Lo anoté: lo hago apenas termine lo que estoy haciendo.');
        break;
      case 'sections':
        window.dispatchEvent(new CustomEvent('sml:sections-changed', {
          detail: { projectId: data.project_id },
        }));
        break;
      default:
        break;
    }
  }, [s]);

  // Envío LMX-T · con LMX trabajando, un pedido nuevo NO se pierde ni arranca
  // otro turno: queda en espera en el servidor y arranca cuando termine.
  const enqueue = useCallback(async (message, projectId) => {
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/lmx/jobs/queue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ project_id: projectId, text: message }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `El servidor respondió ${res.status}`);
      s.getState().setJobs(data);
      toast.success('Lo anoté: arranca apenas termine lo que estoy haciendo.');
      return true;
    } catch (err) {
      toast.error(String(err.message || err).includes('espera')
        ? `${err.message}. Quitá uno de la lista o esperá a que termine alguno.`
        : 'No pude anotar el pedido. Probá de nuevo en unos segundos.');
      return false;
    }
  }, [getToken, s]);

  const send = useCallback(async ({ message, action, projectId, screen, attachments, jobId }) => {
    const store = s.getState();
    if (store.busy) {
      if (message && projectId && !action && !(attachments || []).length) {
        return enqueue(message, projectId);
      }
      return false;
    }
    const atts = (attachments || []).filter((a) => a.assetId);
    if (message || atts.length) {
      store.pushUser(message || 'Te paso esta imagen.', atts.map((a) => a.url || a.preview));
    }
    store.setBusy(true);
    store.setPose('think');
    store.setStatus('Pensando…', true);
    store.setPendingConfirm(null);
    store.notifyOrb();
    // Envío LMX-6b · si el stream termina sin «done» (se cortó la conexión),
    // el turno sigue en el servidor: se avisa y se trae lo que hizo.
    let gotDone = false;
    let failed = false;
    let expired = false;

    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/lmx/turn`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'text/event-stream',
        },
        body: JSON.stringify({
          message: message || null,
          action: action || null,
          project_id: projectId || null,
          conversation_id: store.conversationId,
          job_id: jobId || null,
          context: { screen: screen || 'canvas' },
          attachments: atts.length
            ? atts.map((a) => ({ asset_id: a.assetId, name: a.name }))
            : null,
        }),
      });

      // Envío LMX-T · la aprobación venció (15 min): LMX recotiza sola.
      if (res.status === 409 && action?.type === 'approve') {
        expired = true;
        throw new Error('Esa aprobación venció. Le pido a LMX que vuelva a cotizar.');
      }
      if (!res.ok || !res.body) {
        throw new Error(res.status === 404
          ? 'Lumière no está disponible para tu usuario.'
          : `El servidor respondió ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      for (;;) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop() || '';
        frames.forEach((frame) => {
          const lines = frame.split('\n');
          const evLine = lines.find((l) => l.startsWith('event: '));
          const dataLine = lines.find((l) => l.startsWith('data: '));
          if (!evLine || !dataLine) return;
          let data = {};
          try { data = JSON.parse(dataLine.slice(6)); } catch { /* frame parcial */ }
          const evName = evLine.slice(7).trim();
          if (evName === 'done') gotDone = true;
          if (evName === 'error') failed = true;
          handle(evName, data);
        });
      }
      if (!gotDone && !failed) {
        s.getState().pushCard({
          type: 'tag', level: 'warn',
          text: 'Se cortó la conexión, pero sigo trabajando: en unos segundos te traigo lo que hice.',
        });
        window.dispatchEvent(new CustomEvent('lmx:rehydrate', { detail: { projectId } }));
      }
    } catch (err) {
      s.getState().pushCard({
        type: 'tag', text: err.message || 'Falló el turno', level: 'warn',
      });
      s.getState().setPose('worry');
      s.getState().setStatus('Algo falló', false, true);
    } finally {
      const st = s.getState();
      st.closeTurn();
      st.setBusy(false);
      st.setBuilding(false);
      if (!st.pendingConfirm) {
        st.setPose('idle');
        if (!st.status.warn) st.setStatus('En espera', false);
      }
      // Envío LMX-T · avisa que el turno cerró (arranca la cola, pedido de
      // «Detener y hacer esto», recotizar una aprobación vencida).
      window.dispatchEvent(new CustomEvent('lmx:turn-closed', {
        detail: { projectId, expired },
      }));
    }
    return true;
  }, [enqueue, getToken, handle, s]);

  const approve = useCallback((confirmId, projectId) =>
    send({ action: { type: 'approve', confirm_id: confirmId }, projectId }), [send]);

  const cancel = useCallback((confirmId, projectId) =>
    send({ action: { type: 'cancel', confirm_id: confirmId }, projectId }), [send]);

  return { send, approve, cancel };
};

export default useLmxTurn;
